// src/pages/EnglishPracticeHistoryPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardCard from "../components/DashboardCard"; // Adjust path if needed
import { ArrowRightIcon, RefreshCwIcon } from "../components/Icons"; // Adjust path if needed
import { GetEnglishPracticeSessionsSummary } from "../services/englishPracticeService"; // Adjust path
import Loader from "../components/Loader"; // Assuming you have a Loader component

const EnglishPracticeHistoryPage = () => {
  const navigate = useNavigate();
  const [sessionsList, setSessionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 5,
    totalPages: 0,
    totalRecords: 0,
  });

  const fetchHistory = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await GetEnglishPracticeSessionsSummary(page, pagination.pageSize);
      setSessionsList(response.data || []);
      setPagination({
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalRecords: response.totalRecords,
      });
    } catch (err) {
      console.error("Error fetching English sessions summary:", err);
      setError("Failed to load practice history. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchHistory(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleCardKeyDown = (event, sessionId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/dashboard/english-history/${sessionId}`);
    }
  };

  const handleRedoAssessment = (e, sessionId) => {
    e.stopPropagation();
    navigate(`/english-practice?redoSessionId=${sessionId}`);
  };


  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="min-h-screen flex justify-center items-center bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900">
        <Loader message="Loading practice history..." />
      </div>
    );
  }

  if (error) {
    return (
      <main role="alert" aria-live="assertive" className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 text-white p-4 sm:p-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-red-300"> {/* Adjusted contrast */}
          Error
        </h1>
        <p className="text-slate-300">{error}</p> {/* Adjusted contrast */}
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg border border-slate-600"
        >
          Back to Dashboard
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 text-white p-4 sm:p-8">
      <header className="mb-10 flex items-center justify-between">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100"> {/* Adjusted contrast */}
          English Practice History
        </h1>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm" // Adjusted contrast
        >
          Back to Dashboard
        </button>
      </header>

      {sessionsList.length === 0 ? (
        <DashboardCard>
          <p className="text-center text-slate-400"> {/* Adjusted contrast */}
            No English practice history found. Start a session to see your
            progress!
          </p>
        </DashboardCard>
      ) : (
        <>
          <ul className="space-y-6">
            {sessionsList.map((session) => (
              <li key={session.sessionId}>
                <DashboardCard
                  className="hover:shadow-emerald-400/30 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-900" // Added focus styles
                  onClick={() =>
                    navigate(`/dashboard/english-history/${session.sessionId}`)
                  }
                  role="button" // Changed to button as it triggers an action
                  tabIndex={0}
                  onKeyDown={(e) => handleCardKeyDown(e, session.sessionId)}
                  aria-label={`View details for ${session.planTitle || 'Session ' + session.sessionId}`}
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-emerald-300"> {/* Changed h4 to h2 for better semantics, adjusted contrast */}
                        {session.planTitle || `Session #${session.sessionId}`}
                      </h2>
                      <p className="text-sm text-slate-400"> {/* Adjusted contrast */}
                        Date: {formatDate(session.createUTC)}
                      </p>
                      {session.estimatedProficiencyTarget && (
                        <p className="text-xs text-slate-400"> {/* Adjusted contrast */}
                          Target: {session.estimatedProficiencyTarget}
                        </p>
                      )}
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="text-xl font-bold text-slate-100"> {/* Adjusted contrast */}
                        {session.totalAnsweredQuestion} / {session.totalQuestion}
                      </p>
                      <p className="text-xs text-slate-400">Tasks Completed</p> {/* Adjusted contrast */}
                    </div>
                  </div>
                  <div className="flex justify-end mt-2 space-x-4">
                    <button
                      onClick={(e) => handleRedoAssessment(e, session.sessionId)}
                      className="text-sm text-amber-300 hover:text-amber-200 flex items-center"
                      aria-label={`Redo assessment for ${session.planTitle || 'Session ' + session.sessionId}`}
                    >
                      Redo <RefreshCwIcon className="w-4 h-4 ml-1" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/english-history/${session.sessionId}`);
                      }}
                      className="text-sm text-sky-300 hover:text-sky-200 flex items-center" /* Adjusted contrast */
                      aria-label={`View details for ${session.planTitle || 'Session ' + session.sessionId} (button)`} // More specific label
                    >
                      View Details <ArrowRightIcon aria-hidden="true" className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </DashboardCard>
              </li>
            ))}
          </ul>
          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="mt-10 flex justify-center items-center space-x-4">
              <button
                onClick={() => handlePageChange(pagination.pageNumber - 1)}
                disabled={pagination.pageNumber === 1}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
              >
                Previous
              </button>
              <span className="text-slate-300 text-sm">
                Page {pagination.pageNumber} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.pageNumber + 1)}
                disabled={pagination.pageNumber === pagination.totalPages}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default EnglishPracticeHistoryPage;
